import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button,
  CircularProgress, Divider, Grid, Switch,
  Chip, Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Skeleton,
  Tabs, Tab, useMediaQuery, useTheme,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import ExtensionIcon from '@mui/icons-material/Extension';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import TuneIcon from '@mui/icons-material/Tune';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import {
  getMyClinic, updateMyClinic, updateMyClinicFeatures, exportClinicData,
  getAppointmentTypes, createAppointmentType, updateAppointmentType, deleteAppointmentType,
  getConsultationTypes, createConsultationType, updateConsultationType, deleteConsultationType,
  uploadClinicLogo, downloadClinicLogo, deleteClinicLogo,
  generateDicomApiKey,
} from '../services/api';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNotification } from '../hooks/useNotification';

const MODULES = [
  {
    key: 'billing',
    label: 'Facturación',
    description: 'Registrá pagos por consulta, parciales o reservas. Estados: pagado / pendiente / obra social / exento. Exportable en Excel.',
    badge: 'Nuevo',
  },
  {
    key: 'waiting_room',
    label: 'Sala de espera',
    description: 'Cola de pacientes en tiempo real. El recepcionista recepciona al paciente, el médico lo llama desde su dashboard.',
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    description: 'Enviá recordatorios y confirmaciones por WhatsApp usando plantillas con variables dinámicas ({patient_name}, {fecha}, etc.).',
  },
  {
    key: 'prescriptions',
    label: 'Recetas electrónicas',
    description: 'Emití recetas digitales con medicamentos del vademecum ANMAT. Requiere matrícula del médico. Vencimiento automático a 30 días.',
    badge: 'Nuevo',
  },
  {
    key: 'study_orders',
    label: 'Órdenes de estudio',
    description: 'Emití órdenes digitales de laboratorio, imágenes u otros estudios, firmadas y verificables por QR — mismo criterio que las recetas. Con plantillas reutilizables desde Plantillas → Órdenes de estudio.',
    badge: 'Nuevo',
  },
  {
    key: 'algoritmos',
    label: 'Algoritmos',
    description: 'Árboles de decisión clínicos interactivos con imágenes/video de referencia por nodo — pensado para residentes.',
  },
  {
    key: 'waitlist',
    label: 'Lista de espera',
    description: 'Anotá pacientes que quieren un turno más próximo. Cuando se libera un lugar, se contacta manualmente en orden.',
  },
  {
    key: 'obstetrics',
    label: 'Calculadoras de obstetricia',
    description: 'Biometría fetal (peso estimado y percentilo, curva Hadlock) y checklist de factores de riesgo de preeclampsia (ACOG), disponibles al cargar una consulta. Pensado para gineco-obstetricia — el resto de las especialidades no las ve.',
    badge: 'Nuevo',
  },
  {
    key: 'patient_portal',
    label: 'Portal del paciente',
    description: 'El paciente pide acceso con su email y recibe un link para ver y cancelar sus propios turnos, sin necesitar usuario ni contraseña. Si lo apagás, dejan de enviarse esos links (turnos existentes no se ven afectados).',
    badge: 'Nuevo',
  },
  {
    key: 'dicom_studies',
    label: 'Ingesta automática de estudios (DICOM)',
    description: 'Recibí automáticamente estudios de un ecógrafo u otro equipo médico en la Historia Clínica, sin subirlos a mano — requiere un Orthanc corriendo en la red del consultorio. Configuración avanzada, ver la sección de abajo para generar la clave de conexión.',
    badge: 'Beta',
  },
  {
    key: 'patient_self_update',
    label: 'Link de autocompletado de datos',
    description: 'Generá un link para que un paciente (nuevo o existente) cargue o actualice sus propios datos antes del turno, sin necesitar cuenta — lo compartís vos por WhatsApp. El paciente nunca puede borrar un dato que ya tenías cargado, solo completar lo que falta.',
    badge: 'Nuevo',
  },
];

// Sub-controles del flujo de turnos (siempre activo) — a diferencia de
// MODULES, que prenden/apagan secciones enteras de la app, estos 3 solo
// deciden si se manda o no cada email puntual. Antes no existía ningún
// control: los 3 emails se mandaban siempre, sin forma de apagarlos.
const NOTIFICATION_TOGGLES = [
  {
    key: 'email_confirmacion_turno',
    label: 'Confirmación de turno',
    description: 'Email automático al paciente apenas se crea un turno, con los datos y el link para confirmar o cancelar.',
  },
  {
    key: 'email_recordatorio_turno',
    label: 'Recordatorio de turno',
    description: 'Email automático el día anterior al turno (vía un cron programado). No afecta al widget de recordatorio manual por WhatsApp.',
  },
  {
    key: 'email_cancelacion_turno',
    label: 'Confirmación de cancelación',
    description: 'Email automático al paciente cuando cancela su turno desde el link que recibió por email.',
  },
  {
    key: 'email_agenda_diaria_medico',
    label: 'Agenda diaria al médico',
    description: 'Email al médico (mismo cron que los recordatorios a pacientes) con la lista de sus turnos de mañana: nombre y teléfono del paciente, tipo de turno y si pagó seña o el total.',
  },
];

// Submenús estilo GitHub (nav lateral con secciones separadas, cada una su
// propia URL) — pedido explícito del usuario, referencia directa a cómo lo
// resolvieron en otro proyecto propio. Antes esto era un solo scroll largo
// de 6 Paper apiladas (datos básicos, módulos, DICOM condicional,
// notificaciones, backup, tipos de turno) — un admin que quería tocar un
// solo ajuste tenía que escrollear por todo lo demás primero.
const SECTIONS = [
  { key: 'general', label: 'General', icon: <BusinessIcon fontSize="small" /> },
  { key: 'modulos', label: 'Módulos', icon: <ExtensionIcon fontSize="small" /> },
  { key: 'notificaciones', label: 'Notificaciones', icon: <EmailIcon fontSize="small" /> },
  { key: 'turnos', label: 'Tipos de turno', icon: <EventNoteIcon fontSize="small" /> },
  { key: 'consultas', label: 'Tipos de consulta', icon: <MedicalServicesIcon fontSize="small" /> },
  { key: 'avanzado', label: 'Avanzado', icon: <TuneIcon fontSize="small" /> },
];
const DEFAULT_SECTION = SECTIONS[0].key;

// Clave de conexión para el forwarder de Orthanc (corriendo local en el
// consultorio) — se muestra en texto plano SOLO una vez, justo al generarla
// o regenerarla, porque el backend no la vuelve a exponer después (mismo
// criterio que cualquier secreto de un solo uso).
const DicomApiKeySection = () => {
  const notify = useNotification();
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const handleGenerate = async () => {
    const hasExisting = !!newKey;
    if (hasExisting && !window.confirm(
      'Generar una clave nueva invalida la anterior de inmediato — vas a tener que reconfigurar el Orthanc del consultorio con la nueva. ¿Continuar?'
    )) return;
    setGenerating(true);
    try {
      const res = await generateDicomApiKey();
      setNewKey(res.data.dicom_api_key);
    } catch (err) {
      notify(err.userMessage || 'Error al generar la clave', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(newKey);
    notify('Clave copiada', 'success');
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <VpnKeyIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6">Clave de conexión DICOM</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Esta clave la usa el Orthanc que corre en la red del consultorio para autenticarse al subir estudios —
        no es una contraseña de usuario, no compartas este texto por fuera de esa configuración.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {newKey ? (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.light' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Copiá esta clave ahora — no se va a volver a mostrar.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
              {newKey}
            </Typography>
            <IconButton size="small" onClick={handleCopy}><ContentCopyIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Todavía no generaste una clave para esta clínica.
        </Typography>
      )}

      <Button
        variant={newKey ? 'outlined' : 'contained'}
        startIcon={generating ? <CircularProgress size={16} /> : <VpnKeyIcon />}
        onClick={handleGenerate}
        disabled={generating}
        sx={{ mt: 2 }}
      >
        {newKey ? 'Generar nueva (invalida la anterior)' : 'Generar clave'}
      </Button>
    </Paper>
  );
};

const EMPTY_TYPE_FORM = { name: '', default_duration_minutes: 30, color: '#1565c0' };

const AppointmentTypesSection = ({ noMarginTop = false }) => {
  const notify = useNotification();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TYPE_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getAppointmentTypes({ include_inactive: true });
      setTypes(r.data || []);
    } catch {
      setTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_TYPE_FORM); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, default_duration_minutes: t.default_duration_minutes, color: t.color });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { notify('El nombre es obligatorio', 'warning'); return; }
    setSaving(true);
    try {
      if (editing) await updateAppointmentType(editing.id, form);
      else await createAppointmentType(form);
      setOpen(false);
      await load();
      notify(editing ? 'Tipo de turno actualizado' : 'Tipo de turno creado', 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`¿Eliminar el tipo de turno "${t.name}"?`)) return;
    try {
      const r = await deleteAppointmentType(t.id);
      await load();
      notify(
        r.data?.deactivated
          ? `"${t.name}" tiene turnos asociados — se desactivó en lugar de eliminarlo.`
          : `"${t.name}" eliminado`,
        'success',
      );
    } catch (err) {
      notify(err.userMessage || 'Error al eliminar', 'error');
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await updateAppointmentType(t.id, { is_active: !t.is_active });
      await load();
    } catch (err) {
      notify(err.userMessage || 'Error al actualizar', 'error');
    }
  };

  return (
    <Paper sx={{ p: 3, mt: noMarginTop ? 0 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventNoteIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Tipos de turno</Typography>
        </Box>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nuevo tipo
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Definí los tipos de turno de tu clínica (ej: Consulta, Control, Urgencia) con su duración y color por defecto en el calendario.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Color</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Duración</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.map((t) => (
                <TableRow key={t.id} hover sx={{ opacity: t.is_active ? 1 : 0.5 }}>
                  <TableCell>
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: t.color, border: '1px solid rgba(0,0,0,0.15)' }} />
                  </TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.default_duration_minutes} min</TableCell>
                  <TableCell>
                    <Chip
                      label={t.is_active ? 'Activo' : 'Inactivo'}
                      size="small"
                      color={t.is_active ? 'success' : 'default'}
                      onClick={() => handleToggleActive(t)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(t)}><EditIcon fontSize="small" /></IconButton>
                    <Tooltip title="Si tiene turnos asociados, se desactiva en vez de eliminarse">
                      <IconButton size="small" color="error" onClick={() => handleDelete(t)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {types.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">Todavía no creaste ningún tipo de turno</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Editar tipo de turno' : 'Nuevo tipo de turno'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Nombre *"
            fullWidth
            autoFocus
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Ej: Consulta, Control, Urgencia"
          />
          <TextField
            label="Duración por defecto (minutos)"
            type="number"
            fullWidth
            value={form.default_duration_minutes}
            onChange={(e) => setForm((p) => ({ ...p, default_duration_minutes: Number(e.target.value) }))}
            inputProps={{ min: 5 }}
          />
          <TextField
            label="Color"
            type="color"
            fullWidth
            value={form.color}
            onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const EMPTY_CONSULTATION_TYPE_FORM = { name: '' };

// Gestión del catálogo de "tipo de consulta" (HC) — mismo patrón que
// AppointmentTypesSection arriba, pedido explícito del usuario 2026-10-12.
// A diferencia de tipos de turno, acá no hay color/duración (no aplican a
// una consulta) y "en uso" se chequea por nombre en el backend (ver
// routers/consultation_types.py), porque consultation_type es texto
// libre en MedicalRecord, no una FK real.
const ConsultationTypesSection = ({ noMarginTop = false }) => {
  const notify = useNotification();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CONSULTATION_TYPE_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getConsultationTypes({ include_inactive: true });
      setTypes(r.data || []);
    } catch {
      setTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_CONSULTATION_TYPE_FORM); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name }); setOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { notify('El nombre es obligatorio', 'warning'); return; }
    setSaving(true);
    try {
      if (editing) await updateConsultationType(editing.id, form);
      else await createConsultationType(form);
      setOpen(false);
      await load();
      notify(editing ? 'Tipo de consulta actualizado' : 'Tipo de consulta creado', 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`¿Eliminar el tipo de consulta "${t.name}"?`)) return;
    try {
      const r = await deleteConsultationType(t.id);
      await load();
      notify(
        r.data?.deactivated
          ? `"${t.name}" tiene consultas asociadas — se desactivó en lugar de eliminarlo.`
          : `"${t.name}" eliminado`,
        'success',
      );
    } catch (err) {
      notify(err.userMessage || 'Error al eliminar', 'error');
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await updateConsultationType(t.id, { is_active: !t.is_active });
      await load();
    } catch (err) {
      notify(err.userMessage || 'Error al actualizar', 'error');
    }
  };

  return (
    <Paper sx={{ p: 3, mt: noMarginTop ? 0 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MedicalServicesIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Tipos de consulta</Typography>
        </Box>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nuevo tipo
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Opciones sugeridas al cargar una consulta en Historia Clínica (ej: Primera consulta, Control,
        Urgencia). También se puede crear uno nuevo al vuelo desde el propio formulario de consulta.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.map((t) => (
                <TableRow key={t.id} hover sx={{ opacity: t.is_active ? 1 : 0.5 }}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={t.is_active ? 'Activo' : 'Inactivo'}
                      size="small"
                      color={t.is_active ? 'success' : 'default'}
                      onClick={() => handleToggleActive(t)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(t)}><EditIcon fontSize="small" /></IconButton>
                    <Tooltip title="Si tiene consultas asociadas, se desactiva en vez de eliminarse">
                      <IconButton size="small" color="error" onClick={() => handleDelete(t)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {types.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary">Todavía no creaste ningún tipo de consulta</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Editar tipo de consulta' : 'Nuevo tipo de consulta'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Nombre *"
            fullWidth
            autoFocus
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Ej: Primera consulta, Control, Urgencia"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const ClinicSettings = () => {
  const notify = useNotification();
  const { features, reload: reloadFeatures } = useClinicFeatures();
  const { section } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // /settings a secas (sin :section) redirige a /settings/general — se
  // navega en vez de solo asumir el default acá, para que la URL en la
  // barra del navegador siempre refleje la sección real que se está
  // viendo (consistente con el resto de la app, que nunca deja URLs
  // "genéricas" apuntando a contenido que en realidad cambió).
  const activeSection = SECTIONS.some(s => s.key === section) ? section : DEFAULT_SECTION;
  useEffect(() => {
    if (section !== activeSection) navigate(`/settings/${activeSection}`, { replace: true });
  }, [section, activeSection, navigate]);

  const [clinic, setClinic] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [localFeatures, setLocalFeatures] = useState({});
  const [exporting, setExporting] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    getMyClinic()
      .then(r => {
        setClinic(r.data);
        setForm({ name: r.data.name || '', address: r.data.address || '', phone: r.data.phone || '' });
      })
      .catch(() => notify('No se pudo cargar la información de la clínica.', 'error'))
      .finally(() => setLoading(false));
  }, [notify]);

  // Mismo patrón que PatientAvatar.js — el logo vive atrás de un endpoint
  // autenticado, se trae como blob y se resuelve a una object URL local.
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    setLogoUrl(null);
    if (clinic?.has_logo) {
      downloadClinicLogo()
        .then(res => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(res.data);
          setLogoUrl(objectUrl);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [clinic?.has_logo]);

  const handleLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoUploading(true);
    try {
      const res = await uploadClinicLogo(file);
      setClinic(res.data);
      notify('Logo actualizado', 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al subir el logo', 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    setLogoUploading(true);
    try {
      const res = await deleteClinicLogo();
      setClinic(res.data);
      notify('Logo eliminado', 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al eliminar el logo', 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  // Sync local feature state when context loads
  useEffect(() => {
    setLocalFeatures({ ...features });
  }, [features]);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { notify('El nombre de la clínica es obligatorio.', 'warning'); return; }
    setSaving(true);
    try {
      const res = await updateMyClinic(form);
      setClinic(res.data);
      notify('Cambios guardados correctamente.', 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al guardar los cambios.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const res = await exportClinicData();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Backup_Completo_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      notify('Backup descargado correctamente.', 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al exportar los datos.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleToggleFeature = async (key, enabled) => {
    const next = { ...localFeatures, [key]: enabled };
    setLocalFeatures(next);
    setSavingFeatures(true);
    try {
      await updateMyClinicFeatures(next);
      await reloadFeatures();
      const label = MODULES.find(m => m.key === key)?.label || NOTIFICATION_TOGGLES.find(t => t.key === key)?.label;
      notify(`"${label}" ${enabled ? 'activado' : 'desactivado'}`, enabled ? 'success' : 'info');
    } catch (err) {
      // Revert on error
      setLocalFeatures(prev => ({ ...prev, [key]: !enabled }));
      notify(err.userMessage || 'Error al cambiar el módulo', 'error');
    } finally {
      setSavingFeatures(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 680 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={40} />
            <Skeleton variant="text" width="25%" />
          </Box>
        </Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="text" width="30%" height={32} />
          <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
          <Divider sx={{ mb: 3 }} />
          <Skeleton variant="rounded" height={56} sx={{ mb: 2.5 }} />
          <Skeleton variant="rounded" height={56} sx={{ mb: 2.5 }} />
          <Skeleton variant="rounded" height={56} />
        </Paper>
        <Paper sx={{ p: 3 }}>
          <Skeleton variant="text" width="30%" height={32} />
          <Skeleton variant="text" width="70%" sx={{ mb: 2 }} />
          <Divider sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={80} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 980 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <BusinessIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Configuración de la clínica</Typography>
          {clinic && (
            <Typography variant="body2" color="text.secondary">
              {/* created_at viene naive-UTC del backend — agregar la 'Z' evita
                  que el navegador lo interprete como hora local */}
              ID #{clinic.id} · Creada el {new Date(clinic.created_at.endsWith('Z') ? clinic.created_at : clinic.created_at + 'Z').toLocaleDateString('es-AR')}
              {' · '}Plan {clinic.plan === 'pro' ? 'Pro' : 'Free'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Nav de secciones — vertical en desktop (al costado, como GitHub
          Settings), horizontal arriba en mobile (no hay ancho para una
          columna lateral fija). Tabs de MUI con orientation condicional en
          vez de un Drawer: más liviano para 5 ítems fijos, sin necesitar
          overlay ni breakpoint propio aparte del que ya se usa acá. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
        <Paper sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0, position: { md: 'sticky' }, top: { md: 16 } }}>
          <Tabs
            value={activeSection}
            onChange={(_, val) => navigate(`/settings/${val}`)}
            orientation={isMobile ? 'horizontal' : 'vertical'}
            variant="scrollable"
            scrollButtons={isMobile ? 'auto' : false}
            sx={{
              '& .MuiTab-root': { alignItems: { xs: 'center', md: 'flex-start' }, minHeight: 44, textAlign: 'left' },
              borderRight: { md: 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: 'divider',
            }}
          >
            {SECTIONS.map(s => (
              <Tab key={s.key} value={s.key} label={s.label} icon={s.icon} iconPosition="start"
                sx={{ minHeight: 44, justifyContent: { xs: 'center', md: 'flex-start' } }} />
            ))}
          </Tabs>
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {activeSection === 'general' && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>Datos de la clínica</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Esta información se muestra en reportes y exportaciones.
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {/* Logo — aparece en el PDF de Historia Clínica, el PDF de recetas
                  y la impresión de HC. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{
                  width: 72, height: 72, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                  bgcolor: 'grey.50', flexShrink: 0, position: 'relative',
                }}>
                  {logoUploading && (
                    <CircularProgress size={24} sx={{ position: 'absolute' }} />
                  )}
                  {logoUrl ? (
                    <Box component="img" src={logoUrl} alt="Logo de la clínica" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <BusinessIcon sx={{ color: 'text.disabled', fontSize: 32 }} />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Logo de la clínica</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Aparece en el PDF de Historia Clínica, el PDF de recetas y al imprimir la HC.
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={handleLogoSelected} />
                    <Button size="small" variant="outlined" disabled={logoUploading} onClick={() => logoInputRef.current?.click()}>
                      {clinic?.has_logo ? 'Cambiar' : 'Subir logo'}
                    </Button>
                    {clinic?.has_logo && (
                      <Button size="small" color="error" disabled={logoUploading} onClick={handleDeleteLogo}>
                        Quitar
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2.5}>
                <Grid size={12}>
                  <TextField
                    label="Nombre de la clínica *"
                    fullWidth
                    value={form.name}
                    onChange={set('name')}
                    helperText="Nombre oficial que identifica a esta clínica en el sistema."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Teléfono"
                    fullWidth
                    value={form.phone}
                    onChange={set('phone')}
                    placeholder="Ej: +54 11 4444-5555"
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Dirección"
                    fullWidth
                    value={form.address}
                    onChange={set('address')}
                    placeholder="Calle, número, ciudad"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                >
                  Guardar cambios
                </Button>
              </Box>
            </Paper>
          )}

          {activeSection === 'modulos' && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <ExtensionIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6">Módulos activos</Typography>
                {savingFeatures && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Activá o desactivá funcionalidades para esta clínica. El cambio es inmediato y afecta a todos los usuarios.
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                {MODULES.map(mod => (
                  <Box
                    key={mod.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: localFeatures[mod.key] ? 'primary.light' : 'divider',
                      bgcolor: localFeatures[mod.key] ? 'primary.50' : 'transparent',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{mod.label}</Typography>
                        {mod.badge && <Chip label={mod.badge} size="small" color="primary" sx={{ fontSize: '0.65rem', height: 18 }} />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">{mod.description}</Typography>
                    </Box>
                    <Switch
                      checked={!!localFeatures[mod.key]}
                      onChange={(e) => handleToggleFeature(mod.key, e.target.checked)}
                      disabled={savingFeatures}
                      color="primary"
                    />
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {activeSection === 'notificaciones' && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <EmailIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6">Notificaciones por email</Typography>
                {savingFeatures && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Controlá qué emails automáticos de turno se mandan al paciente. El texto de cada uno se edita desde
                Plantillas → Email.
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                {NOTIFICATION_TOGGLES.map(t => (
                  <Box
                    key={t.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: localFeatures[t.key] ? 'primary.light' : 'divider',
                      bgcolor: localFeatures[t.key] ? 'primary.50' : 'transparent',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                    </Box>
                    <Switch
                      checked={!!localFeatures[t.key]}
                      onChange={(e) => handleToggleFeature(t.key, e.target.checked)}
                      disabled={savingFeatures}
                      color="primary"
                    />
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {activeSection === 'turnos' && <AppointmentTypesSection noMarginTop />}

          {activeSection === 'consultas' && <ConsultationTypesSection noMarginTop />}

          {activeSection === 'avanzado' && (
            <>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <DownloadIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="h6">Exportar mis datos</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Descargá un Excel con toda la información de tu clínica — pacientes, médicos, turnos, pagos,
                  historia clínica y recetas — en hojas separadas. Tus datos son tuyos: podés llevártelos cuando quieras.
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Button
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={handleExportAll}
                  disabled={exporting}
                >
                  Descargar backup completo
                </Button>
              </Paper>

              {localFeatures.dicom_studies && <DicomApiKeySection />}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ClinicSettings;
